/**
 * Static app registry. Drives the sidebar, mobile tabbar, breadcrumbs,
 * and per-app accent tinting. One entry per visible app screen.
 *
 * The tint values are CSS custom property *names* (e.g. ``--c-blue``)
 * — the consuming code wraps them in ``var(--c-blue)`` when applying.
 * This indirection lets every theme define its own concrete colour
 * for the same logical tint.
 */

import { Icon, type IconName } from "../lib/icons";

export type AppId =
  | "today"
  | "tasks"
  | "notes"
  | "cal"
  | "habit"
  | "shop"
  | "cook"
  | "people";

export interface AppMeta {
  id: AppId;
  /** i18n key — pass through ``T()``. */
  i18nKey: string;
  /** CSS custom property name for the per-app tint, e.g. ``--c-blue``. */
  tint: string;
  /** Icon component from the design system. */
  icon: (typeof Icon)[IconName];
}

export const APPS: readonly AppMeta[] = [
  { id: "today", i18nKey: "today", tint: "--accent",    icon: Icon.hub },
  { id: "tasks", i18nKey: "tasks", tint: "--c-purple",  icon: Icon.task },
  { id: "notes", i18nKey: "notes", tint: "--c-yellow",  icon: Icon.note },
  { id: "cal",   i18nKey: "cal",   tint: "--c-blue",    icon: Icon.cal },
  { id: "habit", i18nKey: "habit", tint: "--c-green",   icon: Icon.habit },
  { id: "shop",  i18nKey: "shop",  tint: "--c-orange",  icon: Icon.cart },
  { id: "cook",  i18nKey: "cook",  tint: "--c-red",     icon: Icon.chef },
  { id: "people", i18nKey: "people", tint: "--c-aqua",  icon: Icon.people },
];

export const SETTINGS_APP: AppMeta = {
  id: "today",
  i18nKey: "settings",
  tint: "--c-purple",
  icon: Icon.settings,
};

export function findAppMeta(id: AppId | "settings"): AppMeta {
  if (id === "settings") return SETTINGS_APP;
  const found = APPS.find((a) => a.id === id);
  return found ?? APPS[0]!;
}

/** Map an ``AppId`` to its router path. */
export function appPathOf(id: AppId): string {
  switch (id) {
    case "today": return "/today";
    case "tasks": return "/tasks";
    case "notes": return "/notes";
    case "cal":   return "/calendar";
    case "habit": return "/habits";
    case "shop":  return "/shopping";
    case "cook":  return "/recipes";
    case "people": return "/people";
  }
}
